import { PageHeader } from "@bednbody/ui/components/page-header";
import { Empty } from "@bednbody/ui/components/empty";
import { Button } from "@bednbody/ui/components/button";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Callout } from "@bednbody/ui/components/callout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import type { ColumnDef } from "@tanstack/react-table";

import {
  BuildingsIcon,
  CheckCircleIcon,
  ClockIcon,
  HandCoinsIcon,
  MoneyIcon,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import {
  type RemittanceRow,
  useCashHandoffs,
  useMarkRemittanceCollected,
  useMarkRemittanceReceived,
  useRemittancesOverview,
} from "./cash.hooks";
import { custodyLabel, isOutstandingCash } from "./cash.custody";
import { CASH_HANDLING_LABELS } from "./cash.handoffs";
import { hubReceiptState } from "./cash.queue";
import { CashHandoffsSection } from "./CashHandoffsSection";
import { CashRetentionDialog } from "./CashRetentionDialog";

const RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
] as const;

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

interface ConfirmState {
  kind: "receive" | "collect" | "collect-all";
  rows: RemittanceRow[];
  title: string;
  description: string;
}

/** How far a Collect all got before it stopped — shown instead of one bare error. */
interface CollectProgress {
  done: number;
  total: number;
  failedRef: string | null;
  message: string;
}

/**
 * Cash at the hub and the office (`05 Rules/Cash Handling Rules`).
 *
 * Cash reaches a hub through Cash Handling — a rider's Hub leg, confirmed by the
 * hub manager in the section below — or, when a therapist's pickup had no rider,
 * straight from her, which the hub manager confirms here. Main office then
 * collects what each hub holds. Every move runs through the cash Edge Function,
 * which writes audit_log; role gates mirror its withAuth lists.
 */
export function CashRemittancePage() {
  const { claims } = useAuth();
  const isHubManager = claims?.role === "hub_manager";
  // ⚠ HUB MANAGER, OR SUPER ADMIN (0241). CASH gives Admin oversight of the
  // Hub's cash, not custody of it, and the Edge route still refuses Admin and
  // Executive. Superadmin is the gate for the undefined "Hub recipient" role so
  // cash is not stranded when no manager is on shift (never-invent-policy §2).
  const canReceive = isHubManager || claims?.role === "superadmin";
  const canCollect = claims?.role === "admin" || claims?.role === "superadmin";
  const hubId = isHubManager ? (claims?.hub_id ?? null) : null;

  const [days, setDays] = useState<number>(7);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [collectProgress, setCollectProgress] = useState<CollectProgress | null>(null);
  // Which row's Cash Handling split is open, if any.
  const [splitFor, setSplitFor] = useState<{
    bookingId: string;
    ref: string | null;
    therapist: string | null;
  } | null>(null);

  // ⚠ ADMIN AND SUPERADMIN ONLY, AND THE GRANT IS WHY. `0203` grants
  // `therapist_cash_retentions` to therapist, admin, executive and superadmin —
  // hub_manager is absent, and executive cannot reach /cash at all. CASH names
  // Admin *and* Hub Manager for the balance update, so the omission is either an
  // oversight or "within their access scope" doing that work; withholding the
  // authority rather than granting it myself is `never-invent-policy.md` §2.
  // Recorded as OQ-129.
  const canSeeSplit = claims?.role === "admin" || claims?.role === "superadmin";

  const { data, isLoading, isError } = useRemittancesOverview(hubId, days);
  // Same query key as the Cash Handling section below, so this is one request.
  const { data: handoffs } = useCashHandoffs(hubId, days);
  const markReceived = useMarkRemittanceReceived();
  const markCollected = useMarkRemittanceCollected();

  // Every open row at any age, plus closed history inside the range — the hook
  // loads both (`isOpenRemittance`). Totals and History below use the range.
  const all = useMemo(() => data?.rows ?? [], [data]);
  const inRange = useMemo(() => {
    const since = data?.since;
    return since ? all.filter((r) => (r.created_at ?? "") >= since) : all;
  }, [all, data]);

  const rows = inRange;

  const totals = useMemo(() => {
    const sum = (list: RemittanceRow[]) => list.reduce((s, r) => s + r.amount, 0);
    // ⚠ THE PENDING COUNT EXCLUDES ₱0 ROWS, THE TABLE BELOW DOES NOT. Since
    // 0203 every cash booking opens a remittance and it is ₱0 whenever the
    // Therapist was owed at least what she collected — a record that Cash
    // Handling ran, never a hand-over anybody is waiting for.
    const pending = all.filter(isOutstandingCash);
    const received = all.filter((r) => r.status === "received");
    const collected = inRange.filter((r) => r.status === "collected");
    return {
      // Open money is counted whatever its age; closed money only in range.
      pending: { amount: sum(pending), count: pending.length },
      received: { amount: sum(received), count: received.length },
      collected: { amount: sum(collected), count: collected.length },
      total: { amount: sum(inRange), count: inRange.length },
    };
  }, [all, inRange]);

  // Remittances whose cash is moving through the handoff chain — their Hub leg
  // is confirmed in the Cash Handling section, never here.
  const inHandoffChain = useMemo(
    () => new Set((handoffs ?? []).map((h) => h.remittance_id)),
    [handoffs],
  );

  // Cash a therapist handed STRAIGHT to this hub — the only rows the hub manager
  // confirms here. ₱0 rows and anything a rider is carrying never appear.
  const receiveRows = useMemo(
    () =>
      all
        .map((r) => ({ row: r, state: hubReceiptState(r, hubId, inHandoffChain) }))
        .filter((x): x is { row: RemittanceRow; state: "confirm" | "waiting" } => x.state !== null),
    [all, hubId, inHandoffChain],
  );

  /** "For Pick Up" aggregate per hub (PRD §6.4) — received, awaiting office visit. */
  const pickupByHub = useMemo(() => {
    const groups = new Map<
      string,
      { hubId: string; hubName: string; rows: RemittanceRow[]; total: number }
    >();
    for (const r of all) {
      if (r.status !== "received") continue;
      const g = groups.get(r.hub_id) ?? {
        hubId: r.hub_id,
        hubName: r.hubs?.name ?? "Unknown hub",
        rows: [],
        total: 0,
      };
      g.rows.push(r);
      g.total += r.amount;
      groups.set(r.hub_id, g);
    }
    return [...groups.values()].sort((a, b) => b.total - a.total);
  }, [all]);

  const openConfirm = (next: ConfirmState) => {
    // A new action starts clean — an old failure must not sit above it.
    markReceived.reset();
    markCollected.reset();
    setCollectProgress(null);
    setConfirm(next);
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    const { kind, rows: targets } = confirm;
    setConfirm(null);
    if (kind === "receive") {
      if (targets[0]) markReceived.mutate(targets[0].id);
      return;
    }
    // collect / collect-all: sequential so each row gets its own audited,
    // idempotent Edge Function call; stop at the first failure and say how far
    // it got — the rows before it ARE collected.
    setBatchBusy(true);
    let done = 0;
    try {
      for (const target of targets) {
        await markCollected.mutateAsync(target.id);
        done += 1;
      }
    } catch (err) {
      const failed = targets[done];
      setCollectProgress({
        done,
        total: targets.length,
        failedRef: failed?.bookings?.ref ?? null,
        message: err instanceof Error ? err.message : "Something went wrong.",
      });
    } finally {
      setBatchBusy(false);
    }
  };

  // A Collect-all failure is reported by `collectProgress` instead.
  const mutationError = markReceived.error ?? (collectProgress ? null : markCollected.error);

  const actionsBusy = markReceived.isPending || markCollected.isPending || batchBusy;

  const columns: ColumnDef<RemittanceRow, unknown>[] = [
    {
      id: "therapist",
      accessorFn: (row) => row.therapist?.full_name ?? "",
      header: "Therapist",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.therapist?.full_name ?? "—"}
          subtitle={row.original.bookings?.ref ?? "Booking"}
          fallback={(row.original.therapist?.full_name ?? "TH").slice(0, 2).toUpperCase()}
        />
      ),
    },
    { id: "booking", accessorFn: (row) => row.bookings?.ref ?? "", header: "Booking" },
    ...(!isHubManager
      ? [{ id: "hub", accessorFn: (row: RemittanceRow) => row.hubs?.name ?? "", header: "Hub" }]
      : []),
    {
      id: "submitted",
      accessorFn: (row) => row.created_at ?? "",
      header: "Submitted",
      cell: ({ row }) =>
        row.original.created_at
          ? new Date(row.original.created_at).toLocaleString("en-PH", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      id: "amount",
      accessorFn: (row) => row.amount,
      header: "Amount",
      cell: ({ row }) =>
        canSeeSplit ? (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() =>
              setSplitFor({
                bookingId: row.original.booking_id,
                ref: row.original.bookings?.ref ?? null,
                therapist: row.original.therapist?.full_name ?? null,
              })
            }
          >
            {peso(row.original.amount)}
          </Button>
        ) : (
          peso(row.original.amount)
        ),
    },
    {
      id: "status",
      accessorFn: (row) => row.status ?? "",
      header: "Stage",
      cell: ({ row }) => (
        <>
          <Badge
            variant={
              row.original.status === "collected"
                ? "success"
                : row.original.status === "received"
                  ? "info"
                  : "warning"
            }
            className="capitalize"
          >
            {row.original.status}
          </Badge>
          {row.original.status === "received" && row.original.receiver?.full_name ? (
            <span className="ml-2 text-xs text-muted-foreground">
              by {row.original.receiver.full_name}
            </span>
          ) : null}
          {row.original.status === "collected" && row.original.collector?.full_name ? (
            <span className="ml-2 text-xs text-muted-foreground">
              by {row.original.collector.full_name}
            </span>
          ) : null}
        </>
      ),
    },
    { id: "custody", accessorFn: custodyLabel, header: "With" },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.bookings?.ref ?? "remittance"}
          actions={[
            ...(canSeeSplit
              ? [
                  {
                    label: "View cash split",
                    onSelect: () =>
                      setSplitFor({
                        bookingId: row.original.booking_id,
                        ref: row.original.bookings?.ref ?? null,
                        therapist: row.original.therapist?.full_name ?? null,
                      }),
                  },
                ]
              : []),
            {
              label: "Copy remittance ID",
              onSelect: () => void navigator.clipboard.writeText(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        actions={
          <NativeSelect value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map((r) => (
              <NativeSelectOption key={r.days} value={r.days}>
                {r.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        }
        description={`Cash reaches the hub when ${canReceive ? "you confirm" : "the hub manager confirms"} it, then the main office collects it. Every step is audit-logged. A pending row may still be with the therapist or a rider — the With column says which.`}
        title="Cash Remittance"
      />

      {isError && (
        <Callout tone="danger" role="alert">
          Couldn't load remittances — check your connection and try again.
        </Callout>
      )}
      {mutationError && (
        <Callout tone="danger" role="alert">
          {mutationError.message}
        </Callout>
      )}
      {collectProgress ? (
        <Callout tone="danger" role="alert">
          <p>
            Collected {collectProgress.done} of {collectProgress.total}. Stopped at{" "}
            {collectProgress.failedRef ?? "the next remittance"}: {collectProgress.message} Nothing
            after it was touched.
          </p>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => setCollectProgress(null)}
            className="shrink-0"
          >
            Dismiss
          </Button>
        </Callout>
      ) : null}

      {/* Open money at any age; collected money inside the selected range */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {(
          [
            [ClockIcon, "Pending", totals.pending, "bg-warning/15 text-warning"],
            [HandCoinsIcon, "For pick up", totals.received, "bg-info/15 text-info"],
            [CheckCircleIcon, "Collected in range", totals.collected, "bg-success/15 text-success"],
            [
              MoneyIcon,
              "Total in range",
              totals.total,
              "bg-primary/10 text-primary-dark dark:text-primary-light",
            ],
          ] as const
        ).map(([IconComp, label, t]) => (
          <StatTile
            key={label}
            layout="compact"
            label={label}
            icon={IconComp}
            detail={
              <>
                {t.count} remittance{t.count === 1 ? "" : "s"}
              </>
            }
          >
            {isLoading ? "..." : peso(t.amount)}
          </StatTile>
        ))}
      </div>

      {/* Cash a therapist handed straight to this hub — the one hub receipt the
          handoff chain below does not cover. Hub manager of that hub only. */}
      {canReceive && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">
            Cash handed to your hub{" "}
            <span className="font-normal text-muted-foreground">
              — from a therapist with no pickup rider; riders&rsquo; cash is under Cash Handling
            </span>
          </h2>
          {isLoading ? (
            <Skeleton className="h-16 motion-reduce:animate-none" />
          ) : receiveRows.length === 0 ? (
            <Empty>No cash waiting for your hub to confirm.</Empty>
          ) : (
            <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {receiveRows.map(({ row: r, state }) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {r.therapist?.full_name ?? "Therapist"}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {r.bookings?.ref ?? ""}
                      </span>
                    </p>
                    <p className="text-sm font-semibold tabular-nums text-primary-dark dark:text-primary-light">
                      {peso(r.amount)}
                    </p>
                  </div>
                  {/* CASH: the recipient's confirm stays disabled until the
                      payer has confirmed handing it over. */}
                  {state === "confirm" ? (
                    <Button
                      variant="default"
                      size="sm"
                      type="button"
                      disabled={actionsBusy}
                      onClick={() =>
                        openConfirm({
                          kind: "receive",
                          rows: [r],
                          title: `${CASH_HANDLING_LABELS.recipientConfirm}?`,
                          description: `Count ${peso(r.amount)} from ${r.therapist?.full_name ?? "the therapist"} (${r.bookings?.ref ?? "booking"}) before confirming. Take exactly this amount — hand back anything over it. This moves the cash off her balance and is audit-logged.`,
                        })
                      }
                      className="shrink-0"
                    >
                      {CASH_HANDLING_LABELS.recipientConfirm}
                    </Button>
                  ) : (
                    <p className="shrink-0 text-xs text-muted-foreground">
                      Waiting for her to hand it over
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* For Pick Up per hub — main office's stage */}
      {canCollect && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">
            For pick up by hub{" "}
            <span className="font-normal text-muted-foreground">— collect on hub visit</span>
          </h2>
          {isLoading ? (
            <Skeleton className="h-24 motion-reduce:animate-none" />
          ) : pickupByHub.length === 0 ? (
            <Empty>Nothing waiting for office pick-up.</Empty>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {pickupByHub.map((g) => (
                <SurfaceCard key={g.hubId}>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground">
                      <BuildingsIcon size={16} className="shrink-0 text-primary" />
                      <span className="truncate">{g.hubName}</span>
                    </p>
                    <Button
                      variant="default"
                      size="sm"
                      type="button"
                      disabled={actionsBusy}
                      onClick={() =>
                        openConfirm({
                          kind: "collect-all",
                          rows: g.rows,
                          title: `Collect all cash at ${g.hubName}?`,
                          description: `${g.rows.length} remittance${g.rows.length === 1 ? "" : "s"} totalling ${peso(g.total)}. Each one is marked Collected individually and audit-logged.`,
                        })
                      }
                      className="shrink-0"
                    >
                      Collect all · {peso(g.total)}
                    </Button>
                  </div>
                  <ul className="divide-y divide-border">
                    {g.rows.map((r) => (
                      <li key={r.id} className="flex items-center justify-between gap-3 py-2">
                        <p className="min-w-0 truncate text-sm text-foreground">
                          {r.therapist?.full_name ?? "Therapist"}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {r.bookings?.ref ?? ""}
                            {r.receiver?.full_name ? ` · received by ${r.receiver.full_name}` : ""}
                          </span>
                        </p>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="text-sm font-medium tabular-nums">{peso(r.amount)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            disabled={actionsBusy}
                            onClick={() =>
                              openConfirm({
                                kind: "collect",
                                rows: [r],
                                title: "Mark cash as collected?",
                                description: `${peso(r.amount)} from ${r.therapist?.full_name ?? "therapist"} at ${g.hubName}. This closes out the remittance and is audit-logged.`,
                              })
                            }
                          >
                            Collect
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </SurfaceCard>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Cash Handling — the three handoffs (0179–0181), and the only place the
          Hub leg can be confirmed. Above History because two of the three legs
          are live money somebody is carrying right now. */}
      <CashHandoffsSection days={days} />

      {/* History */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">History</h2>
        <AdminDataTable
          presentation="standalone"
          data={rows}
          columns={columns}
          getRowId={(row) => row.id}
          searchText={(row) =>
            [row.therapist?.full_name, row.bookings?.ref, row.hubs?.name, custodyLabel(row)].join(
              " ",
            )
          }
          searchPlaceholder="Search therapist, ref, hub, or custodian…"
          facets={[
            {
              id: "status",
              label: "Stage",
              options: [
                { value: "pending", label: "Pending" },
                { value: "received", label: "For pick up" },
                { value: "collected", label: "Collected" },
              ],
              getValue: (row) => row.status ?? "",
            },
          ]}
          initialSorting={[{ id: "submitted", desc: true }]}
          loading={isLoading}
          emptyDescription="Try a wider date range or a different filter."
          exportFilename="cash-remittances"
          exportScopeLabel="up to 1,000 loaded rows"
          exportColumns={[
            { header: "Therapist", value: (row) => row.therapist?.full_name },
            { header: "Booking", value: (row) => row.bookings?.ref },
            { header: "Hub", value: (row) => row.hubs?.name },
            { header: "Submitted", value: (row) => row.created_at },
            { header: "Amount", value: (row) => row.amount },
            { header: "Stage", value: (row) => row.status },
            { header: "Custody", value: custodyLabel },
          ]}
        />
      </section>

      {/* Confirm dialog — cash stage moves are one-way, so never one-click */}
      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CASH § Visibility's "balance update" — mounted once beside the confirm
          dialog rather than per row, so the table renders one of these and not
          one per remittance. */}
      <CashRetentionDialog
        bookingId={splitFor?.bookingId ?? null}
        bookingRef={splitFor?.ref ?? null}
        therapistName={splitFor?.therapist ?? null}
        allowed={canSeeSplit}
        onClose={() => setSplitFor(null)}
      />
    </div>
  );
}
